import React, { useState, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import AlbumIcon from "@mui/icons-material/Album";
import MicIcon from "@mui/icons-material/Mic";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import { GoogleLogin } from "@react-oauth/google";
import SeoHead from "../Components/SEO/SeoHead";
import CommunityPreview from "../Components/Gallery/CommunityPreview";
import { BASE_API_URL, BASE_SITE_URL } from "../config";

const LandingPage = (props: any) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({ albumCount: 0, artistCount: 0, songCount: 0 });
  const [randomLoading, setRandomLoading] = useState(false);

  useEffect(() => {
    fetch(`${BASE_API_URL}/api/stats`)
      .then((res) => res.json())
      .then((data) => setStats(data))
      .catch(() => {});
  }, []);

  if (props.state.page !== "landingPage") {
    props.basicFunctions.updateState({ page: "landingPage" });
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: t("common.siteName"),
    url: BASE_SITE_URL,
    description: t("home.seoDescription"),
    potentialAction: {
      "@type": "SearchAction",
      target: `${BASE_SITE_URL}/tier-list/albums?search={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  const statItems = [
    { label: t("home.statsAlbums"), count: stats.albumCount || 0, icon: <AlbumIcon sx={{ fontSize: 40 }} /> },
    { label: t("home.statsArtists"), count: stats.artistCount || 0, icon: <MicIcon sx={{ fontSize: 40 }} /> },
    { label: t("home.statsSongs"), count: stats.songCount || 0, icon: <MusicNoteIcon sx={{ fontSize: 40 }} /> },
  ];

  return (
    <Box
      id="landing-page"
      sx={{
        backgroundColor: "background.default",
        color: "text.primary",
        height: "100vh",
        overflow: "hidden auto",
      }}
    >
      <SeoHead
        title={t("home.seoTitle")}
        description={t("home.seoDescription")}
        path="/"
        keywords={t("home.keywords", { returnObjects: true }) as string[]}
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <Box
        id="landing-hero"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          py: { xs: 8, md: 12 },
          px: 3,
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            mb: 2,
            fontSize: { xs: "2rem", sm: "2.75rem", md: "3.5rem" },
          }}
        >
          {t("home.heroTitle")}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            maxWidth: 640,
            mb: 4,
            color: "var(--text-secondary)",
            fontWeight: 400,
            lineHeight: 1.6,
            fontSize: { xs: "1rem", md: "1.15rem" },
          }}
        >
          {t("home.heroSubtitle")}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
          <Button
            component="a"
            href="/tier-list/albums"
            variant="contained"
            size="large"
            sx={{
              px: 5,
              py: 1.5,
              fontSize: "1.1rem",
              fontWeight: 700,
              borderRadius: 3,
              textTransform: "none",
              backgroundColor: "var(--accent)",
              "&:hover": {
                backgroundColor: "var(--accent-hover)",
              },
            }}
          >
            {t("home.ctaButton")}
          </Button>
          <Button
            variant="outlined"
            size="large"
            disabled={randomLoading}
            startIcon={<ShuffleIcon />}
            onClick={async () => {
              setRandomLoading(true);
              try {
                const res = await fetch(`${BASE_API_URL}/api/random-tierlist`);
                const data = await res.json();
                const params = new URLSearchParams();
                if (data.listType) params.set("listType", data.listType);
                if (data.groupBy) params.set("groupBy", data.groupBy);
                const lt = (data.listType || "albums").toLowerCase();
                const gb = data.groupBy;
                window.location.href = gb
                  ? `/tier-list/${lt}/${encodeURIComponent(gb)}`
                  : `/tier-list/${lt}`;
              } catch {
                window.location.href = "/tier-list/albums";
              }
            }}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
              fontWeight: 700,
              borderRadius: 3,
              textTransform: "none",
              borderColor: "var(--border-mid)",
              color: "var(--text-primary)",
              "&:hover": {
                borderColor: "var(--border-strong)",
                backgroundColor: "var(--hover-overlay)",
              },
            }}
          >
            {t("home.randomButton")}
          </Button>
        </Box>
        {!props.state.isAuthenticated && (
          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
            <GoogleLogin
              onSuccess={(credentialResponse) => {
                if (credentialResponse.credential) {
                  props.mainFunctions.handleGoogleSignIn(credentialResponse.credential);
                }
              }}
              onError={() => console.error("Google Sign-In failed")}
            />
          </Box>
        )}
      </Box>

      {/* Stats Section */}
      <Box
        id="landing-stats"
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: { xs: 3, md: 6 },
          px: 3,
          py: 5,
          flexWrap: "wrap",
        }}
      >
        {statItems.map((item) => (
          <Box
            key={item.label}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              minWidth: 120,
            }}
          >
            <Box sx={{ color: "primary.main" }}>{item.icon}</Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {item.count.toLocaleString()}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "var(--text-secondary)" }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Community Preview */}
      <CommunityPreview {...props} />
    </Box>
  );
};

export default LandingPage;
